/**
 * ProjectService - unified project list rendering and management
 * Eliminates duplicated updateBrowserProjectList/updateCloudProjectList code
 */

/**
 * Normalized project info that works for both storage backends
 */
export interface ProjectInfo {
  id: string | number;
  name: string;
  isActive: boolean;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Render a project list to a container element
 * This is the unified rendering logic used by both cloud and browser storage
 */
export function renderProjectList(
  container: HTMLElement,
  projects: ProjectInfo[],
  options?: {
    emptyMessage?: string;
  }
): void {
  const emptyMessage = options?.emptyMessage ?? 'No projects';

  if (projects.length === 0) {
    container.innerHTML = `<div class="sidebar-project-item" style="color: var(--text-muted)">${emptyMessage}</div>`;
    return;
  }

  container.innerHTML = projects.map(p => `
    <div class="sidebar-project-item ${p.isActive ? 'active' : ''}" data-project-id="${p.id}">
      ${escapeHtml(p.name)}
      <div class="project-actions">
        <button class="btn-project-rename" data-rename-project-id="${p.id}" title="Rename project">
          <svg xmlns="http://www.w3.org/2000/svg" height="12" viewBox="0 -960 960 960" width="12" fill="currentColor"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
        </button>
        <button class="btn-project-delete" data-delete-project-id="${p.id}" title="Delete project">&times;</button>
      </div>
    </div>
  `).join('');
}

/**
 * Storage keys for persisting last used project
 */
const LAST_PROJECT_KEY_CLOUD = 'leedoc_last_project_cloud';
const LAST_PROJECT_KEY_BROWSER = 'leedoc_last_project_browser';

/**
 * Save the last used project ID
 */
export function saveLastProject(mode: 'cloud' | 'browser', projectId: string | number): void {
  const key = mode === 'cloud' ? LAST_PROJECT_KEY_CLOUD : LAST_PROJECT_KEY_BROWSER;
  localStorage.setItem(key, String(projectId));
}

/**
 * Get the last used project ID
 */
export function getLastProject(mode: 'cloud' | 'browser'): string | null {
  const key = mode === 'cloud' ? LAST_PROJECT_KEY_CLOUD : LAST_PROJECT_KEY_BROWSER;
  return localStorage.getItem(key);
}

/**
 * ProjectService manages project listing and selection
 */
export class ProjectService {
  private projectNameEl: HTMLElement | null = null;
  private projectListEl: HTMLElement | null = null;

  /**
   * Initialize with DOM elements
   */
  init(projectNameEl: HTMLElement | null, projectListEl: HTMLElement | null): void {
    this.projectNameEl = projectNameEl;
    this.projectListEl = projectListEl;
  }

  /**
   * Update the project name display
   */
  setProjectName(name: string | null): void {
    if (this.projectNameEl) {
      this.projectNameEl.textContent = name ?? 'Select project...';
    }
  }

  /**
   * Render the project list
   */
  renderList(projects: ProjectInfo[]): void {
    if (!this.projectListEl) return;
    renderProjectList(this.projectListEl, projects);
  }

  /**
   * Show error state in the project list
   */
  showError(message: string = 'Error loading projects'): void {
    if (this.projectListEl) {
      this.projectListEl.innerHTML = `<div class="sidebar-project-item" style="color: var(--text-muted)">${message}</div>`;
    }
  }
}

/**
 * Singleton project service instance
 */
export const projectService = new ProjectService();
